// src/components/pages/WatchingPage.js

import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Box,
  Grid,
  Button,
} from "@mui/material";
import { useTheme } from "styled-components"; // Keeping as per your preference
import { useAuth } from "../../context/AuthContext";
import {
  doc,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../../firebase";

const WatchingPage = () => {
  const theme = useTheme();
  const { currentUser, loading: authLoading, error: authError } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [videoDetails, setVideoDetails] = useState(null);
  const [markers, setMarkers] = useState([]); // State for servedMarkers
  const [loading, setLoading] = useState(true); // Loading state for data fetching
  const [error, setError] = useState(null); // Error state for data fetching

  // Fetch User Profile with Real-Time Listener
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const userDocRef = doc(db, "users", currentUser.uid);

    const unsubscribe = onSnapshot(
      userDocRef,
      (userSnap) => {
        if (userSnap.exists()) {
          const userData = userSnap.data();
          setUserProfile(userData);
          // Reset videoDetails and markers when userProfile is fetched
          setVideoDetails(null);
          setMarkers([]);
        } else {
          setError("User profile does not exist.");
          setVideoDetails(null);
          setMarkers([]);
        }
      },
      (err) => {
        console.error("Error fetching user profile:", err);
        setError("Failed to fetch user profile.");
        setVideoDetails(null);
        setMarkers([]);
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [currentUser]);

  // Fetch Video Details with Real-Time Listener
  useEffect(() => {
    if (
      !userProfile ||
      !userProfile.currentlyWatching ||
      !userProfile.currentlyWatching.videoId
    ) {
      setVideoDetails(null);
      setLoading(false);
      return;
    }

    const { videoId } = userProfile.currentlyWatching;
    const videoDocRef = doc(db, "videos", videoId);

    const unsubscribe = onSnapshot(
      videoDocRef,
      (videoSnap) => {
        if (videoSnap.exists()) {
          const videoData = videoSnap.data();
          setVideoDetails(videoData);
        } else {
          setError("Video does not exist.");
          setVideoDetails(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching video details:", err);
        setError("Failed to fetch video details.");
        setVideoDetails(null);
        setLoading(false);
      }
    );

    // Cleanup listener on unmount or when videoId changes
    return () => unsubscribe();
  }, [userProfile]);

  // Set Up Firestore Listener for servedMarkers with Real-Time Updates
  useEffect(() => {
    let unsubscribe = () => {};

    const fetchMarkers = () => {
      if (
        !userProfile ||
        !userProfile.currentlyWatching ||
        !userProfile.currentlyWatching.sessionId
      ) {
        setMarkers([]); // Clear markers if sessionId is not present
        return;
      }

      const { sessionId } = userProfile.currentlyWatching;

      // Create a query against the servedMarkers collection
      const markersQuery = query(
        collection(db, "servedMarkers"),
        where("sessionId", "==", sessionId),
        orderBy("createdAt", "desc")
      );

      // Set up real-time listener
      unsubscribe = onSnapshot(
        markersQuery,
        (querySnapshot) => {
          const markersData = [];
          querySnapshot.forEach((doc) => {
            markersData.push({ id: doc.id, ...doc.data() });
          });
          setMarkers(markersData);
        },
        (err) => {
          console.error("Error fetching markers:", err);
          setError("Failed to fetch markers.");
        }
      );
    };

    fetchMarkers();

    // Cleanup listener on unmount or when dependencies change
    return () => {
      unsubscribe();
    };
  }, [userProfile]);

  // Handle Loading States
  if (authLoading || loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress sx={{ color: theme.colors.red }} size={80} />
      </Box>
    );
  }

  // Handle Authentication Errors
  if (authError) {
    return (
      <Container maxWidth="lg">
        <Typography
          variant="h6"
          color="error"
          sx={{ fontFamily: "basic-sans" }}
        >
          Authentication Error: {authError.message}
        </Typography>
      </Container>
    );
  }

  // Handle General Errors
  if (error) {
    return (
      <Container maxWidth="lg">
        <Typography
          variant="h6"
          color="error"
          sx={{ fontFamily: "basic-sans" }}
        >
          Error: {error}
        </Typography>
      </Container>
    );
  }

  // Handle Not Logged-In Users
  if (!currentUser) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h6" sx={{ fontFamily: "basic-sans" }}>
          You are not logged in.
        </Typography>
      </Container>
    );
  }

  // Handle Users Not Currently Watching Any Video
  if (
    !userProfile ||
    !userProfile.currentlyWatching ||
    !userProfile.currentlyWatching.videoId ||
    !userProfile.currentlyWatching.sessionId
  ) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h6" sx={{ fontFamily: "basic-sans" }}>
          You are not currently watching any video.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mb: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ fontFamily: "basic-sans" }}>
        Now Watching
      </Typography>

      {videoDetails ? (
        <Card
          sx={{
            display: {
              xs: "block", // Stack content on extra-small screens
              sm: "flex", // Row layout on small screens and up
            },
            width: "100%", // Ensure the card takes the full width of the container
            backgroundColor: "transparent",
            mb: 4,
          }}
        >
          <CardMedia
            component="img"
            sx={{
              width: { xs: "100%", sm: "300px" }, // Full width on extra-small screens; fixed width on small screens and up
              height: "auto",
            }}
            image={videoDetails.thumbnail}
            alt={videoDetails.title}
          />
          <CardContent
            sx={{
              flexGrow: 1, // Make the content fill the remaining space
              backgroundColor: theme.colors.mediumPurple,
            }}
          >
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontFamily: "basic-sans",
                color: "white",
                fontWeight: "700",
                fontSize: "1.4rem",
                mb: 2,
              }}
            >
              {videoDetails.title}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                fontFamily: "basic-sans",
                color: theme.colors.lightGray,
                fontSize: "0.9rem",
                lineHeight: "1.6rem",
              }}
            >
              {videoDetails.description}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Typography variant="h6" sx={{ fontFamily: "basic-sans" }}>
          Loading video details...
        </Typography>
      )}

      {/* Served Markers Stream */}
      <Typography
        variant="h5"
        gutterBottom
        sx={{ fontFamily: "basic-sans", mt: 4 }}
      >
        Interactions
      </Typography>

      {markers.length > 0 ? (
        <Grid container spacing={2}>
          {markers.map((marker) => (
            <Grid item xs={12} md={4} key={marker.id}>
              <Card
                sx={{
                  p: 2,
                  backgroundColor: theme.colors.mediumPurple,
                  display: "flex",
                  flexDirection: "column", // Always stack items vertically
                  alignItems: "center",
                }}
              >
                {/* Marker Media (Image or Video) */}
                {marker.mediaType === "image" ? (
                  <Box
                    component="img"
                    src={marker.mediaURL}
                    alt={marker.title}
                    sx={{
                      width: "100%", // Full width of the card
                      height: "auto",
                      mb: 2, // Add spacing below the image
                      borderRadius: 1,
                    }}
                  />
                ) : marker.mediaType === "video" ? (
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%", // Full width of the card
                      paddingTop: "56.25%", // 16:9 Aspect Ratio
                      mb: 2, // Add spacing below the video
                    }}
                  >
                    <Box
                      component="video"
                      src={marker.mediaURL}
                      controls
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        borderRadius: 1,
                      }}
                    />
                  </Box>
                ) : null}

                {/* Marker Content */}
                <CardContent sx={{ width: "100%", textAlign: "left" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "basic-sans",
                      mb: 1,
                      color: "white",
                      fontWeight: "700",
                    }}
                  >
                    {marker.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "basic-sans",
                      mb: 2,
                      color: theme.colors.lightGray,
                      lineHeight: "1.6rem",
                    }}
                  >
                    {marker.description}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      window.open(
                        marker.callToActionURL,
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                    sx={{
                      width: "100%", // Make the button full width
                      backgroundColor: theme.colors.blue,
                      color: theme.colors.white,
                      fontFamily: "basic-sans",
                      fontWeight: "700",
                      textTransform: "none",
                      padding: "10px 20px",
                      borderRadius: "50px",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: theme.colors.green,
                        boxShadow: "none",
                      },
                    }}
                  >
                    {marker.callToAction}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography variant="body1" sx={{ fontFamily: "basic-sans", mt: 2 }}>
          Waiting for interactions...
        </Typography>
      )}
    </Container>
  );
};

export default WatchingPage;
